<template>
  <v-defaults-provider :defaults="themeDefaults">
    <ConfirmDialog />
    <v-app v-if="newCelusVersion">
      <NewCelusVersionDialog></NewCelusVersionDialog>
    </v-app>
    <router-view v-else-if="$route.meta.outsideNormalLayout"> </router-view>
    <InvalidUserPage v-else-if="invalidUser"></InvalidUserPage>
    <EmailNotVerified
      v-else-if="
        user &&
        !user.impersonator &&
        !emailVerified &&
        (!user.is_superuser || otpRequired)
      "
    ></EmailNotVerified>
    <StandardLayout
      v-else-if="bootUpFinished && !showLoginDialog && !otpRequired"
    ></StandardLayout>
    <div v-else>
      <v-app>
        <BootUpWidget></BootUpWidget>
        <LoginDialog v-if="showLoginDialog"></LoginDialog>
        <OtpDialog v-else-if="otpRequired"></OtpDialog>
      </v-app>
    </div>
  </v-defaults-provider>
</template>

<script>
import LoginDialog from "@/components/account/LoginDialog";
import OtpDialog from "@/components/account/OtpDialog";
import BootUpWidget from "@/components/BootUpWidget";
import NewCelusVersionDialog from "@/components/NewCelusVersionDialog";
import ConfirmDialog from "@/components/util/ConfirmDialog";
import { mapActions, mapGetters, mapState } from "vuex";
import EmailNotVerified from "./EmailNotVerified";
import InvalidUserPage from "./InvalidUserPage";
import StandardLayout from "./StandardLayout";
import { computed, provide } from "vue";
import { useTheme } from "vuetify";
import echartsDarkTheme from "echarts/lib/theme/dark.js";
import { THEME_KEY } from "vue-echarts";
import { sequentialRamp } from "@/libs/charts";
import { DEFAULT_THEME, rememberTheme } from "@/plugins/vuetify";

const transparentEchartsDarkTheme = {
  ...echartsDarkTheme,
  backgroundColor: "transparent",
};

export default {
  name: "Dashboard",
  components: {
    LoginDialog,
    OtpDialog,
    BootUpWidget,
    ConfirmDialog,
    InvalidUserPage,
    NewCelusVersionDialog,
    StandardLayout,
    EmailNotVerified,
  },
  setup() {
    const theme = useTheme();
    const themeDefaults = computed(() => {
      const card = {
        elevation: theme.current.value.variables["card-elevation"] ?? 1,
        // VCard `border` prop value, e.g. "thin outline-variant opacity-100"
        border: theme.current.value.variables["card-border"],
      };
      // VDialog resets defaults to the createVuetify ones for its content and
      // only re-applies what is nested under its own key, so repeat the card
      // defaults there.
      return { VCard: card, VDialog: { VCard: card } };
    });

    // ECharts theme for every chart. `gradientColor` is the default visualMap
    // ramp, so heatmaps that do not set their own colors use the theme tokens.
    provide(
      THEME_KEY,
      computed(() => ({
        ...(theme.current.value.dark ? transparentEchartsDarkTheme : {}),
        gradientColor: sequentialRamp(theme),
      })),
    );

    return { theme, themeDefaults };
  },
  data() {
    return {};
  },
  computed: {
    ...mapState({
      invalidUser: "invalidUser",
      user: "user",
      showLoginDialog: "showLoginDialog",
      otpRequired: "otpRequired",
      newCelusVersion: "newCelusVersion",
    }),
    ...mapGetters({
      loggedIn: "loggedIn",
      bootUpFinished: "bootUpFinished",
      emailVerified: "emailVerified",
    }),
    userTheme() {
      // null until the user is loaded, so the theme remembered from the last
      // visit stays in place during boot
      if (!this.user) return null;
      return this.user.preferences?.theme || DEFAULT_THEME;
    },
    appLanguage: {
      get() {
        return this.$store.state.appLanguage;
      },
      set(newValue) {
        this.$store.dispatch("setAppLanguage", { lang: newValue });
        this.$router.go();
      },
    },
  },
  methods: {
    ...mapActions({
      start: "start",
    }),
    syncLocale() {
      this.$i18n.locale = this.appLanguage;
      this.$vuetify.locale.current = this.appLanguage;
    },
  },
  created() {
    this.start();
  },
  async mounted() {
    this.syncLocale();
  },
  watch: {
    appLanguage() {
      this.syncLocale();
    },
    userTheme: {
      handler(name) {
        if (!name) return;
        // an unknown name (e.g. a since renamed theme) is ignored by Vuetify
        // with a console warning, so the current theme stays
        this.theme.change(name);
        rememberTheme(name);
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss">
img.avatar {
  font-size: 1.25rem;
}

section.header {
  margin-bottom: 2rem;
}

div.fields {
  margin-top: 1rem;
}

div.v-input {
  &.short {
    flex-grow: 0;
  }

  div.v-input__control {
    div.v-text-field__details {
      min-height: 1px;
      .v-messages {
        min-height: 1px;
      }
    }
  }
}

.top-dialog {
  position: absolute !important;
  top: 0 !important;

  @media screen and (min-height: 720px) {
    top: 2vh !important;
  }
}

table.overview-card {
  padding: 1rem;
  background-color: rgb(var(--v-theme-surface-container-low));
  color: rgb(var(--v-theme-on-surface-container-low));
  th {
    font-size: 1rem;
  }
  tr.header {
    th {
      font-variant: small-caps;
      font-weight: 500;
      border-bottom: solid 1px rgb(var(--v-theme-outline-variant));
      padding-top: 0.5rem;
    }
    &:first-child {
      th {
        padding-top: 0;
      }
    }
  }
  td {
    font-size: 1rem;
  }

  th {
    text-align: left;
    padding-right: 1.5rem;
    font-weight: 300;

    &.subdued-th {
      color: rgb(var(--v-theme-on-surface-variant));
    }
  }
}

table.overview {
  th {
    text-align: left;
    padding-right: 1.5rem;
  }

  &.full-width {
    width: 100%;
  }
}

/* data and time formatting */
span.time {
  font-weight: 300;
  font-size: 87.5%;
}

ul.no-bullets {
  list-style: none;
  padding-left: 0;
}

.markdown {
  h3 {
    margin-bottom: 0.5rem;
  }
  h4 {
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
  }
  p {
    margin-bottom: 1rem;
  }
}

.chart-value {
  font-weight: bold;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  text-align: right;
  padding-left: 1rem;
}

.chart-missing-data {
  color: #ff6633;
}

#date-range-selector {
  transition:
    background-color 0.2s,
    border 0.2s;
  border: solid 4px transparent;
  border-radius: 6px;
}

ul.unobtrusive-bullets {
  list-style-type: square;
  padding: 0 0 0 1rem;
  margin: 0;
}

th.align-top {
  vertical-align: top;
}

td.auto-width,
th.auto-width {
  width: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.auto-table .v-data-table__wrapper table {
  th:last-child,
  td:last-child {
    width: 100%;
  }

  th:not(:last-child),
  td:not(:last-child) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 6rem;

    &:first-child {
      min-width: 1.5rem;
    }
  }

  th.text-right {
    // only tds in right aligned columns should be right aligned, not the ths
    text-align: left !important;
    display: flex;
    align-items: center;
    justify-content: space-around;
  }
}

.v-tab {
  text-transform: uppercase;
}

.small-caps {
  font-variant: small-caps;
}

.text-normal {
  font-size: 1rem;
}
</style>
